
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface TransactionData {
  organization_id: string;
  plan_id: string;
  amount: number;
  is_yearly: boolean;
  external_id?: string;
}

export async function createTransaction(data: TransactionData) {
  try {
    console.log('Création d\'une nouvelle transaction:', data);
    
    const { data: transaction, error } = await supabase
      .from('payment_transactions')
      .insert({
        organization_id: data.organization_id,
        plan_id: data.plan_id,
        amount: data.amount,
        is_yearly: data.is_yearly,
        external_id: data.external_id,
        status: 'pending',
        user_id: (await supabase.auth.getUser()).data.user?.id
      })
      .select()
      .single();

    if (error) {
      console.error('Erreur création transaction:', error);
      throw error;
    }

    return transaction;
  } catch (error) {
    console.error('Exception création transaction:', error);
    toast.error('Erreur lors de la création de la transaction');
    throw error;
  }
}

export async function updateTransactionStatus(
  transactionId: string, 
  status: 'completed' | 'failed' | 'pending',
  externalData?: any
) {
  try {
    console.log('Mise à jour du statut de la transaction:', { transactionId, status });
    
    const { error } = await supabase
      .from('payment_transactions')
      .update({
        status,
        external_data: externalData,
        completed_at: status === 'completed' ? new Date().toISOString() : null
      })
      .eq('id', transactionId);

    if (error) {
      console.error('Erreur mise à jour statut transaction:', error);
      throw error;
    }
  } catch (error) {
    console.error('Exception mise à jour statut transaction:', error);
    toast.error('Erreur lors de la mise à jour de la transaction');
    throw error;
  }
}

export async function getTransactionHistory(organizationId: string) {
  try {
    const { data, error } = await supabase
      .from('payment_transactions')
      .select(`
        *,
        subscription_plans (
          name,
          display_name
        )
      `)
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur récupération historique transactions:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Exception récupération historique transactions:', error);
    toast.error('Erreur lors de la récupération de l\'historique des transactions');
    return [];
  }
}

export async function getTransactionById(transactionId: string) {
  try {
    const { data, error } = await supabase
      .from('payment_transactions')
      .select(`
        *,
        subscription_plans (
          name,
          display_name
        )
      `)
      .eq('id', transactionId)
      .single();

    if (error) {
      console.error('Erreur récupération transaction:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Exception récupération transaction:', error);
    toast.error('Erreur lors de la récupération de la transaction');
    return null;
  }
}

export async function resumeTransaction(transactionId: string) {
  try {
    // Récupérer les détails de la transaction
    const transaction = await getTransactionById(transactionId);
    
    if (!transaction) {
      throw new Error('Transaction non trouvée');
    }
    
    if (transaction.status !== 'pending') {
      throw new Error('Cette transaction n\'est pas en attente');
    }
    
    // Si la transaction a déjà une URL Stripe, on peut la réutiliser
    if (transaction.external_data?.url) {
      return { url: transaction.external_data.url };
    }
    
    // Sinon, on doit créer une nouvelle session Stripe
    // Déterminer l'URL de retour (basée sur l'environnement actuel)
    const returnUrl = `${window.location.origin}/dashboard/settings`;
    
    // Appeler le service Stripe avec les informations de la transaction
    const stripeResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/resume-transaction`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
      },
      body: JSON.stringify({
        transactionId,
        returnUrl
      })
    });
    
    if (!stripeResponse.ok) {
      const errorText = await stripeResponse.text();
      throw new Error(`Erreur du serveur: ${errorText}`);
    }
    
    const data = await stripeResponse.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    return { url: data.url };
    
  } catch (error) {
    console.error('Exception reprise transaction:', error);
    toast.error('Erreur lors de la reprise de la transaction');
    throw error;
  }
}
